import { CookieManager } from './cookie-manager.js';
import { LocalStorageManager } from './local-storage-manager.js';

// Only the user id follows the configured storage type: as a cookie it can be
// shared across subdomains. Everything else (visibility lists, order ids, UTM
// data) can be rebuilt from the server and always lives in localStorage, so
// cookies stay small and never exceed the request header limits of customer
// servers.
export class StorageManager {
    static COOKIE_ELIGIBLE_KEYS = ['ph_user_id'];

    constructor(type = 'local', cookieDays = 365) {
        this.cookie = new CookieManager(cookieDays);
        this.local = new LocalStorageManager();

        this.type = null;
        this.storage = null;

        this.PH_KEYS = [
            'ph_survey_completed',
            'ph_survey_at_least_one_answer',
            'ph_user_id',
            'ph_survey_viewed',
            'ph_order_ids',
            'ph_utm_data',
        ];

        // Keys written by older widget versions. They are no longer read and
        // are removed from both stores once so they stop bloating cookies.
        this.LEGACY_KEYS = ['ph_question_viewed', 'ph_survey_prob', 'ph_last_answer'];

        this.removeLegacyKeys();
        this.migrateLocalOnlyKeys();
        this.setStoreType(type);
    }

    removeLegacyKeys() {
        this.LEGACY_KEYS.forEach((key) => {
            this.cookie.remove(key);
            this.local.remove(key);
        });
    }

    isCookieEligible(key) {
        return StorageManager.COOKIE_ELIGIBLE_KEYS.includes(key);
    }

    storeFor(key) {
        return this.isCookieEligible(key) ? this.storage : this.local;
    }

    // Older widget versions kept every key in the cookie when the project was
    // configured for cookies. Move those values to localStorage once and drop
    // the cookies, regardless of the configured type.
    migrateLocalOnlyKeys() {
        this.PH_KEYS.filter((key) => !this.isCookieEligible(key)).forEach((key) => {
            const cookieValue = this.cookie.get(key);
            if (cookieValue != null) {
                if (this.local.get(key) == null) {
                    this.local.set(key, cookieValue);
                }
                this.cookie.remove(key);
            }
        });
    }

    setStoreType(type) {
        if (this.type === type) return;

        const newStorage = type === 'local' ? this.local : this.cookie;
        this.migrateToStorage(newStorage);

        this.type = type;
        this.storage = newStorage;
    }

    migrateToStorage(newStorage) {
        StorageManager.COOKIE_ELIGIBLE_KEYS.forEach((key) => {
            let value = this.cookie.get(key) ?? this.local.get(key);
            if (value != null) {
                newStorage.set(key, value);

                if (newStorage !== this.cookie) this.cookie.remove(key);
                if (newStorage !== this.local) this.local.remove(key);
            }
        });
    }

    set(key, value) {
        this.storeFor(key).set(key, value);
    }

    get(key) {
        return this.storeFor(key).get(key);
    }

    remove(key) {
        this.storeFor(key).remove(key);
    }
}
